import { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import api from '../../../../../services/apiInstance';
import { uploadContent } from '../../../../../services/contents';
import { makeApiCall } from '../../../../../services/apiErrorHandlers';
import useBreakpoint from './useBreackpoint';

const SESSION_KEY = 'libraryRowCount';
const MOBILE_BREAKPOINTS = ['xs', 'sm', 'md'];
const MAX_ROWS = 4;
const DEFAULT_CARD_WIDTH = 134;
const DEFAULT_CARD_GAP = 13;
const SORT_VALUES = ['name', 'oldest', 'newest'];

export const translateTagName = (name, t) => {
  if (name === 'Por defecto') return t('default');
  if (/^Default user \(\d+\)$/.test(name)) return t('my_content');
  return name;
};

const useLibraryContents = props => {
  const { t } = useTranslation();
  const {
    user,
    brand,
    token,
    getContentsByCompany,
    getContentsByUsers,
    setLibraryContents,
    libraryContents,
    updateDefaultContentRequest,
    defaultContent,
    fileCanvaNew,
    statusNewContent,
    setStatusNewContent,
    cardWidth = DEFAULT_CARD_WIDTH,
    cardGap = DEFAULT_CARD_GAP,
    reservedSlots = 1,
    initialVersion = 'V1'
  } = props;

  const defaultTag = { id: null, key: null, label: 'Todos', name: t('All') };

  const getInitialRowCount = () => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    return saved ? parseInt(saved, 10) : 1;
  };

  const containerRef = useRef(null);
  const [itemsPerRow, setItemsPerRow] = useState(8);
  const [rowCount, setRowCount] = useState(getInitialRowCount);
  const [pageContents, setPageCampaigns] = useState(1);
  const [totalContent, setTotalContent] = useState(0);
  const [lengthPage, setLengthPage] = useState(Math.max(1, getInitialRowCount() * 8 - reservedSlots));
  const [tagsContents, setTagasContents] = useState([]);
  const [selectTagContent, setSelectTagContent] = useState(null);
  const [selectVersion, setSelectVersion] = useState(initialVersion);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [sort, setSort] = useState('newest');
  const [folderSubmitting, setFolderSubmitting] = useState(false);

  const breakpoint = useBreakpoint();

  const uploadProps = uploadContent(
    t,
    message,
    token,
    setStatusNewContent,
    setLoading,
    selectTagContent
  );

  const isMobile = () => MOBILE_BREAKPOINTS.includes(breakpoint);

  const getSearchParams = () => {
    const regex = /(\d+)\s*x\s*(\d+)/;
    const match = search.match(regex);
    return {
      search: { value: !match ? search : null, regex: false },
      columns: [
        {
          data: !match ? 'name' : null,
          searchable: true,
          orderable: false,
          search: { value: '', regex: false }
        },
        {
          data: 'width',
          searchable: true,
          orderable: false,
          search: { value: match ? match[1] : '', regex: false }
        },
        {
          data: 'height',
          searchable: true,
          orderable: false,
          search: { value: match ? match[2] : '', regex: false }
        }
      ]
    };
  };

  const getLibraryContents = (page, tagFolder = null) => {
    if (tagsContents.length == 0) return;

    let tag = tagFolder ? tagFolder.key : selectTagContent ? selectTagContent.key : null;
    let startPage = (page - 1) * lengthPage;
    let params = {
      start: startPage,
      length: lengthPage,
      page,
      draw: 1,
      datatable: true,
      origin: 'dsp',
      type: typeFilter ? [typeFilter] : ['image', 'video']
    };

    if (tag) params.tag = tag;
    if (search) params = { ...params, ...getSearchParams() };
    if (SORT_VALUES.includes(sort)) params.sort = sort;

    if (user.company) {
      getContentsByCompany(user.company.id, params);
    } else {
      getContentsByUsers(user.id, params);
    }
  };

  const fetchTagsContent = async () => {
    try {
      const response = await makeApiCall(api.getTagsContents, user.company.id);
      const translatedTags = response.data.map(tag => ({
        ...tag,
        name: translateTagName(tag.name, t)
      }));
      setTagasContents(translatedTags);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error(error.message);
    }
  };

  const fetchTagsContentForUser = async () => {
    try {
      const response = await makeApiCall(api.getTagsContentsByUser, user.id);
      const translatedTags = response.data.map(tag => ({
        ...tag,
        name: translateTagName(tag.name, t)
      }));
      setTagasContents(translatedTags);
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error(error.message);
    }
  };

  const checkReadyContent = id => {
    updateDefaultContentRequest(id);
  };

  const setReadyContent = () => {
    const copyStatusNewContent = {
      ...statusNewContent,
      fileList: [...statusNewContent.fileList.slice(1)]
    };
    if (statusNewContent.fileList.length === 1) {
      const copyLibraryContents = {
        ...libraryContents,
        data: libraryContents.data ? [...libraryContents.data] : []
      };
      copyLibraryContents.data[0] = defaultContent;
      setLibraryContents(copyLibraryContents);
    }
    setStatusNewContent(copyStatusNewContent);
    setLoading(false);
  };

  const changePageContents = page => {
    setPageCampaigns(page);
    getLibraryContents(page, selectTagContent);
  };

  const handleRowCountChange = newRowCount => {
    const newLengthPage = Math.max(1, newRowCount * itemsPerRow - reservedSlots);
    const firstItemIndex = (pageContents - 1) * lengthPage;
    const newPage = Math.max(1, Math.floor(firstItemIndex / newLengthPage) + 1);
    sessionStorage.setItem(SESSION_KEY, String(newRowCount));
    setRowCount(newRowCount);
    setLengthPage(newLengthPage);
    setPageCampaigns(newPage);
  };

  const rowOptions = Array.from({ length: MAX_ROWS }, (_, i) => {
    const rows = i + 1;
    const totalItems = rows * itemsPerRow;
    const rowWord = t(rows === 1 ? 'row' : 'rows');
    return { value: rows, label: `${rows} ${rowWord} (${totalItems} ${t('ads')})` };
  });

  const onChangeFolder = value => {
    value.key = value.key === 'null' ? null : value.key;
    setSelectTagContent(value);
  };

  const onChangeVersion = value => setSelectVersion(value);

  const onSearch = e => {
    setPageCampaigns(1);
    setSearch(e.target.value);
  };

  const onTypeFilterChange = value => {
    setPageCampaigns(1);
    setTypeFilter(value || null);
  };

  const onSortChange = value => {
    setPageCampaigns(1);
    setSort(value || null);
  };

  const refetchTags = () => (user.company ? fetchTagsContent() : fetchTagsContentForUser());

  const createFolder = async (name, parentId) => {
    setFolderSubmitting(true);
    try {
      await makeApiCall(api.createContentTag, parentId ? { name, parent_id: parentId } : { name });
      await refetchTags();
      return true;
    } catch (error) {
      message.error(t(error.message));
      return false;
    } finally {
      setFolderSubmitting(false);
    }
  };

  const renameFolder = async (id, name, parentId) => {
    setFolderSubmitting(true);
    try {
      await makeApiCall(api.updateContentTag, id, { name, parent_id: parentId == null ? null : parentId });
      await refetchTags();
      return true;
    } catch (error) {
      message.error(t(error.message));
      return false;
    } finally {
      setFolderSubmitting(false);
    }
  };

  const deleteFolders = async ids => {
    setFolderSubmitting(true);
    try {
      await makeApiCall(api.archiveContentTags, ids);
      await refetchTags();
      return true;
    } catch (error) {
      message.error(t(error.message));
      return false;
    } finally {
      setFolderSubmitting(false);
    }
  };

  const isOwnerDefaultContent = selectTag => {
    const companyOwnerOfDefaultContent = 757;
    const companyId = user.company ? user.company.id : null;
    if (selectTag && selectTag.key === '1' && companyId != companyOwnerOfDefaultContent)
      return false;
    return true;
  };

  // Effects
  useEffect(() => {
    setLibraryContents([]);
    if (user.company) {
      fetchTagsContent();
    } else {
      fetchTagsContentForUser();
    }
    setStatusNewContent(null);
  }, []);

  const hasResolvedDefaultTagRef = useRef(false);
  useEffect(() => {
    if (!tagsContents || tagsContents.length === 0) return;
    if (hasResolvedDefaultTagRef.current) return;
    hasResolvedDefaultTagRef.current = true;
    const brandTags = brand
      ? tagsContents.find(tag => tag.brand_id === brand.id) || defaultTag
      : defaultTag;
    setSelectTagContent({ key: brandTags.id, label: brandTags.name });
  }, [tagsContents, brand]);

  useEffect(() => {
    getLibraryContents(1);
  }, [selectTagContent]);
  useEffect(() => {
    getLibraryContents(pageContents, selectTagContent);
  }, [search]);
  useEffect(() => {
    getLibraryContents(1, selectTagContent);
  }, [typeFilter, sort]);
  useEffect(() => {
    if (!lengthPage) return;
    getLibraryContents(pageContents, selectTagContent);
  }, [lengthPage]);

  useEffect(() => {
    if (!libraryContents || !libraryContents.data) return;
    if (search) return setTotalContent(libraryContents.recordsFiltered);
    setTotalContent(libraryContents.recordsTotal);
  }, [libraryContents]);

  useEffect(() => {
    if (!statusNewContent || !statusNewContent.fileList || !statusNewContent.fileList.length)
      return;
    const uploadedId = statusNewContent.fileList[0].response && statusNewContent.fileList[0].response.id;
    getLibraryContents(1, selectTagContent);
    if (uploadedId) checkReadyContent(uploadedId);
  }, [statusNewContent]);

  useEffect(() => {
    if (!fileCanvaNew || !fileCanvaNew.resp || !fileCanvaNew.resp.data) return;
    getLibraryContents(1, selectTagContent);
    checkReadyContent(fileCanvaNew.resp.data.id);
  }, [fileCanvaNew]);

  useEffect(() => {
    if (!statusNewContent || !statusNewContent.fileList || !statusNewContent.fileList.length)
      return;
    if (defaultContent.type !== 'video') return;
    const id = statusNewContent.fileList[0].response && statusNewContent.fileList[0].response.id;
    if (!id) return;
    if (defaultContent.id === id && defaultContent.file_thumb !== 'pending')
      return setReadyContent();
    checkReadyContent(id);
    setLoading(true);
  }, [defaultContent]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const cols = Math.max(1, Math.floor((width + cardGap) / (cardWidth + cardGap)));
      setItemsPerRow(cols);
    });
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardWidth, cardGap]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const newLengthPage = Math.max(1, rowCount * itemsPerRow - reservedSlots);
    if (newLengthPage !== lengthPage) {
      setLengthPage(newLengthPage);
      setPageCampaigns(1);
    }
  }, [itemsPerRow]);

  return {
    // State
    containerRef,
    pageContents,
    totalContent,
    lengthPage,
    rowCount,
    rowOptions,
    isMobile,
    tagsContents,
    selectTagContent,
    selectVersion,
    loading,
    search,
    typeFilter,
    sort,
    uploadProps,
    folderSubmitting,
    // Handlers
    changePageContents,
    handleRowCountChange,
    onChangeFolder,
    onChangeVersion,
    onSearch,
    onTypeFilterChange,
    onSortChange,
    isOwnerDefaultContent,
    createFolder,
    renameFolder,
    deleteFolders,
    getLibraryContents
  };
};

export default useLibraryContents;
